import { useTranslation } from '../i18n'
import type { Device } from '../lib/types'
import { COLORS } from '../lib/theme'
import { BatteryIndicator, PageHeader } from './atoms'
import { Icon } from './Icon'

function SignalIndicator({ bars }: { bars: number }) {
  const total = 5
  const color = bars >= 4 ? COLORS.primary : bars >= 2 ? COLORS.tertiary : COLORS.error
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 16 }}>
      {[...Array(total)].map((_, i) => (
        <div
          key={i}
          style={{
            width: 3,
            height: 4 + i * 2.5,
            background: i < bars ? color : 'rgba(32,80,43,0.15)',
            borderRadius: 1,
          }}
        />
      ))}
      <span style={{ fontSize: 12, fontWeight: 600, color, marginLeft: 6 }}>
        {bars}/{total}
      </span>
    </div>
  )
}

export interface DevicesPageProps {
  devices: Device[]
}

export function DevicesPage({ devices }: DevicesPageProps) {
  const { t } = useTranslation('devices')
  return (
    <div>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle', { count: devices.length })}
        actions={
          <button className="wh-btn wh-btn-primary">
            <Icon name="plus" size={16} />
            {t('addDevice')}
          </button>
        }
      />
      <div className="wh-card">
        <div
          className="wh-devices-header"
          style={{
            padding: '14px 18px',
            borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`,
            fontSize: 11,
            fontWeight: 700,
            color: COLORS.outline,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}
        >
          <div>{t('table.device')}</div>
          <div>{t('table.serial')}</div>
          <div>{t('table.pairedWith')}</div>
          <div>{t('table.battery')}</div>
          <div>{t('table.signal')}</div>
          <div>{t('table.lastSeen')}</div>
          <div></div>
        </div>
        {devices.map((d, i) => (
          <div
            key={d.id}
            className="wh-devices-row"
            style={{
              padding: '14px 18px',
              borderBottom:
                i < devices.length - 1 ? `1px solid ${COLORS.surfaceContainerHighest}` : 'none',
            }}
          >
            <div className="wh-devices-cell-device" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  background: COLORS.primaryContainer,
                  color: COLORS.primary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Icon name="device" size={20} />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700 }}>{d.name}</div>
                <div style={{ fontSize: 12, color: COLORS.outline }}>{d.model}</div>
              </div>
            </div>
            <div
              className="wh-devices-cell-serial"
              style={{
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: 12,
                color: COLORS.outline,
              }}
            >
              {d.serial}
            </div>
            <div className="wh-devices-cell-paired" style={{ fontSize: 13 }}>
              {d.paired ? (
                <b>{d.paired}</b>
              ) : (
                <span style={{ color: COLORS.outline }}>{t('notPaired')}</span>
              )}
            </div>
            <div className="wh-devices-cell-battery"><BatteryIndicator pct={d.battery} /></div>
            <div className="wh-devices-cell-signal"><SignalIndicator bars={d.signal} /></div>
            <div className="wh-devices-cell-lastseen" style={{ fontSize: 12, color: COLORS.outline }}>{d.lastSeen}</div>
            <div className="wh-devices-cell-actions" style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
              <button className="wh-btn-icon">
                <Icon name="edit" size={16} />
              </button>
              <button className="wh-btn-icon">
                <Icon name="more" size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
